import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import type { DirectorKpiCard } from "@/api/types";

/**
 * S5 Block 1 — Business KPI row (org-wide, source-gated, mockup v3). Counts are
 * backend-owned; the card click routes to a filtered org-wide drilldown. States:
 * 🔴 C1 revenue/AR data-unavailable → «Дані недоступні» (NEVER stale ₴, AC-DH-SOURCE-001) ·
 * zero→neutral · error→error-state · disabled→card hidden (hide-if-module-off, §0).
 * sales-plan-progress is absent from the payload entirely (no plan source).
 */

// FE owns the exact route+filter (real pages only — §0 no dead-CTA).
const ROUTE: Record<string, string | null> = {
  revenue_30d: "/clients",
  ar_overdue: "/clients?debtStatus=has_overdue",
  active_b2b_clients: "/clients",
  contract_attention: "/contracts?filter=attention",
  clients_no_next_action: "/clients?dataQuality=no_next_action",
  critical_complaints: "/complaints?filter=sla",
  internal_requests_sla: "/requests?filter=sla",
};

const LABEL: Record<string, string> = {
  revenue_30d: "Дохід (30 днів)",
  ar_overdue: "AR прострочено",
  active_b2b_clients: "Активні B2B клієнти",
  contract_attention: "Контракти, що потребують дії",
  clients_no_next_action: "Клієнти без наступної дії",
  critical_complaints: "Критичні рекламації",
  internal_requests_sla: "Запити SLA-breach",
};

const SUB: Record<string, string> = {
  revenue_30d: "rolling-actual (факт), не план",
  ar_overdue: "BI · потребує дії",
  active_b2b_clients: "org-wide",
  contract_attention: "потребує дії",
  clients_no_next_action: "NextActionResolver",
  critical_complaints: "SLA-breach",
  internal_requests_sla: "SLA прострочено",
};

const MONEY = new Set(["revenue_30d", "ar_overdue"]);
const ALERT = new Set(["ar_overdue", "critical_complaints"]); // red
const WARN = new Set(["contract_attention", "clients_no_next_action", "internal_requests_sla"]); // amber

function money(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `₴ ${(v / 1_000_000).toFixed(2)}М`;
  if (Math.abs(v) >= 1_000) return `₴ ${Math.round(v / 1_000)}К`;
  return `₴ ${Math.round(v)}`;
}

export function DirectorKpiGrid({ kpis }: { kpis: DirectorKpiCard[] }) {
  const navigate = useNavigate();
  // disabled cards (module off) hidden — no dead card (§0).
  const visible = kpis.filter((k) => k.sourceStatus !== "disabled");

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {visible.map((k) => (
        <KpiCell key={k.key} card={k} onOpen={(r) => navigate(r)} />
      ))}
    </div>
  );
}

function KpiCell({ card, onOpen }: { card: DirectorKpiCard; onOpen: (route: string) => void }) {
  const label = LABEL[card.key] ?? card.key;
  const route = ROUTE[card.key] ?? null;
  const isError = card.sourceStatus === "error";
  const isUnavail = card.sourceStatus === "data-unavailable";
  const isMoney = MONEY.has(card.key);
  const positive = typeof card.value === "number" && card.value > 0;
  const tone =
    isError || isUnavail ? "muted" : positive && ALERT.has(card.key) ? "alert" : positive && WARN.has(card.key) ? "warn" : "neutral";
  const clickable = route !== null && !isError && !isUnavail;

  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={() => clickable && route && onOpen(route)}
      className={cn(
        "group relative rounded-xl border bg-card p-4 text-left transition-colors",
        clickable ? "cursor-pointer hover:border-[hsl(var(--primary)/0.5)]" : "cursor-default",
        (isError || isUnavail) && "border-dashed",
        tone === "alert" && "border-[hsl(var(--destructive)/0.25)] bg-[hsl(var(--destructive)/0.06)]",
        tone === "warn" && "border-[hsl(var(--warning)/0.5)] bg-[hsl(var(--warning))]",
        tone === "muted" || tone === "neutral" ? "border-border" : "",
      )}
    >
      <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
        <span>{label}</span>
        {clickable && <ChevronRight className="h-3.5 w-3.5 opacity-40 transition-opacity group-hover:opacity-100" />}
      </div>

      {isError ? (
        <div className="mt-2.5 text-sm font-semibold text-[hsl(var(--destructive))]">⚠ Помилка завантаження</div>
      ) : isUnavail ? (
        <>
          <div className="mt-2.5 text-sm font-semibold text-muted-foreground">Дані недоступні</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">BI недоступний — не показуємо застаріле</div>
        </>
      ) : (
        <>
          <div
            className={cn(
              "mt-1.5 text-2xl font-bold tabular-nums tracking-tight",
              tone === "alert" && "text-[hsl(var(--destructive))]",
              tone === "warn" && "text-[hsl(var(--warning-foreground))]",
            )}
          >
            {card.value === null ? "—" : isMoney ? money(card.value) : card.value.toLocaleString("uk-UA")}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">{SUB[card.key] ?? ""}</div>
        </>
      )}
    </button>
  );
}
